import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus } from "lucide-react";
import { useApp } from "../context/AppContext";
import { formatPrice } from "../i18n/translations";

const CartDrawer = () => {
  const { cartOpen, setCartOpen, cart, products, updateQty, removeFromCart, cartSubtotal, currency, t } = useApp();
  const nav = useNavigate();

  return (
    <>
      <div
        className={`fixed inset-0 bg-[var(--noir)]/50 backdrop-blur-sm z-[70] transition-opacity duration-500 ${cartOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={() => setCartOpen(false)}
      />
      <aside
        className={`fixed top-0 ${t.dir === "rtl" ? "left-0" : "right-0"} bottom-0 w-full max-w-[440px] bg-[var(--ivory)] z-[80] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${cartOpen ? "translate-x-0" : t.dir === "rtl" ? "-translate-x-full" : "translate-x-full"} flex flex-col`}
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--champagne)]/50">
          <div className="font-display text-2xl tracking-wide">{t.cart.title}</div>
          <button onClick={() => setCartOpen(false)} data-testid="close-cart-drawer"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p className="text-[var(--grey)] mb-6">{t.cart.empty}</p>
              <button onClick={() => setCartOpen(false)} className="text-xs tracking-[0.2em] uppercase link-lux">
                {t.cart.continueShopping}
              </button>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--champagne)]/40">
              {cart.map((item) => {
                const p = products.find((p) => p.id === item.product_id);
                if (!p) return null;
                const name = (typeof window !== "undefined" && localStorage.getItem("luxera_lang") === "de") ? (p.name_de || p.name) : (p.name_en || p.name);
                return (
                  <li key={item.product_id} className="flex gap-4 py-5" data-testid={`cart-item-${item.product_id}`}>
                    <img src={p.images[0]} alt={name} className="w-20 h-24 object-cover bg-[var(--ivory-deep)]" />
                    <div className="flex-1 flex flex-col">
                      <div className="font-serif text-base leading-snug">{name}</div>
                      <div className="text-sm text-[var(--grey)] mt-1">{formatPrice(p.price, currency)}</div>
                      <div className="flex items-center justify-between mt-auto">
                        <div className="flex items-center border border-[var(--champagne)]">
                          <button className="px-2 py-1" onClick={() => updateQty(item.product_id, item.quantity - 1)} data-testid={`qty-minus-${item.product_id}`}><Minus className="w-3 h-3" /></button>
                          <span className="px-3 text-sm">{item.quantity}</span>
                          <button className="px-2 py-1" onClick={() => updateQty(item.product_id, item.quantity + 1)} data-testid={`qty-plus-${item.product_id}`}><Plus className="w-3 h-3" /></button>
                        </div>
                        <button className="text-[10px] tracking-[0.2em] uppercase text-[var(--grey)] hover:text-[var(--noir)]" onClick={() => removeFromCart(item.product_id)} data-testid={`remove-${item.product_id}`}>
                          {t.cart.remove}
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {cart.length > 0 && (
          <div className="border-t border-[var(--champagne)]/50 px-6 py-5 bg-white">
            <div className="flex justify-between mb-1">
              <span className="text-xs tracking-[0.2em] uppercase">{t.cart.subtotal}</span>
              <span className="font-serif text-lg">{formatPrice(cartSubtotal, currency)}</span>
            </div>
            <p className="text-[11px] text-[var(--grey)] mb-4">{t.cart.tax}</p>
            <button
              onClick={() => { setCartOpen(false); nav("/checkout"); }}
              className="btn-lux w-full bg-[var(--noir)] text-[var(--ivory)] py-4 text-xs tracking-[0.25em] uppercase hover:text-[var(--gold)]"
              data-testid="checkout-btn"
            >
              {t.cart.checkout}
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default CartDrawer;
